// Hook — Web Push enablement for the current device.
//
// Bridges three surfaces:
//   1. navigator.serviceWorker          — register /sw.js, get a
//                                          ServiceWorkerRegistration
//   2. registration.pushManager         — subscribe / unsubscribe with
//                                          the project's VAPID public key
//   3. public.push_subscriptions        — persist the resulting
//                                          { endpoint, p256dh, auth }
//                                          so the edge function can
//                                          dispatch to this device.
//
// Exposes:
//   supported    — boolean; push + serviceWorker available?
//   permission   — "default" | "granted" | "denied"
//   enabled      — boolean; did the current device subscribe AND hand
//                  us a matching row in push_subscriptions?
//   busy         — boolean; enable / disable in flight
//   enable()     — request permission (if needed), subscribe, persist
//   disable()    — unsubscribe + delete the DB row
//
// Opinionated: we DO NOT prompt for permission on mount. Every call
// path is explicit — user taps Enable in Settings. Browsers treat
// automatic prompts as hostile and will quietly block future prompts
// if the user dismisses a few, so we spend our single shot on a
// surface that has user intent behind it.

import { useCallback, useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { supabase } from "./supabase";

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";

// iOS Capacitor path. WKWebView has no PushManager API, so the W3C
// Web Push code below silently bails out (supported=false). Instead
// we register with APNs via @capacitor/push-notifications, receive
// the device token, and upsert it into the SAME push_subscriptions
// table with platform='ios' so send-push can fan out to both web and
// iOS endpoints from one query.
const IS_IOS_NATIVE =
  typeof window !== "undefined"
  && Capacitor.isNativePlatform?.()
  && Capacitor.getPlatform?.() === "ios";

const APP_BUNDLE_ID = "app.booda.ios";

// base64url (no padding, URL-safe) → Uint8Array, which is what
// PushManager.subscribe expects for applicationServerKey.
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// ArrayBuffer → base64url — used for the keys we send back to the DB.
function ab2b64url(buf) {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function useWebPush(userId) {
  // "supported" gates the UI. iOS native counts as supported even
  // though the W3C APIs aren't there — we route through Capacitor.
  const supported = IS_IOS_NATIVE
    || (typeof window !== "undefined"
        && "serviceWorker" in navigator
        && "PushManager"    in window
        && !!VAPID_PUBLIC_KEY);

  const [permission, setPermission] = useState(() => {
    if (IS_IOS_NATIVE) return "default";
    return typeof Notification !== "undefined" ? Notification.permission : "default";
  });
  const [enabled, setEnabled] = useState(false);
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState(null);

  // On mount, read the current subscription status. If the SW is
  // already registered and we have an active PushSubscription, we
  // consider this device "enabled" — but we only trust it if the DB
  // row is present too, so the Settings UI doesn't lie if the user
  // subscribed on one account and is now signed in as another.
  useEffect(() => {
    let alive = true;
    if (!supported || !userId) {
      setEnabled(false);
      return;
    }
    (async () => {
      try {
        // iOS: there's no "getSubscription" idiom — Capacitor doesn't
        // expose the current token without re-calling register(). We
        // check the DB for ANY ios row tied to this user as a proxy
        // for "already enabled"; the actual token gets re-stamped on
        // every register() anyway so a stale row is self-correcting.
        if (IS_IOS_NATIVE) {
          const { data, error: err } = await supabase
            .from("push_subscriptions")
            .select("id")
            .eq("user_id", userId)
            .eq("platform", "ios")
            .limit(1)
            .maybeSingle();
          if (err) console.error("[push] ios status check failed:", err);
          if (!alive) return;
          setEnabled(!!data);
          return;
        }

        const reg = await navigator.serviceWorker.getRegistration("/sw.js");
        if (!reg) { if (alive) setEnabled(false); return; }
        const sub = await reg.pushManager.getSubscription();
        if (!sub) { if (alive) setEnabled(false); return; }

        const { data, error: err } = await supabase
          .from("push_subscriptions")
          .select("id")
          .eq("user_id", userId)
          .eq("endpoint", sub.endpoint)
          .maybeSingle();
        if (err) { console.error("[push] status check failed:", err); }
        if (!alive) return;
        setEnabled(!!data);
      } catch (e) {
        console.error("[push] init failed:", e);
        if (alive) setEnabled(false);
      }
    })();
    return () => { alive = false; };
  }, [supported, userId]);

  const enable = useCallback(async () => {
    if (!supported || !userId || busy) return;
    setBusy(true);
    setError(null);
    try {
      // ── iOS branch (Capacitor) ─────────────────────────────────
      // Request APNs permission, register with the system, capture
      // the device token via the 'registration' event, upsert into
      // push_subscriptions with platform='ios'. The Capacitor plugin
      // delivers the token asynchronously after register() resolves,
      // so we wait on a one-shot listener.
      if (IS_IOS_NATIVE) {
        const perm = await PushNotifications.requestPermissions();
        setPermission(perm.receive); // 'granted' | 'denied' | 'prompt'
        if (perm.receive !== "granted") {
          throw new Error("Notification permission was not granted");
        }

        const token = await new Promise((resolve, reject) => {
          let settled = false;
          const handles = [];
          const cleanup = () => {
            for (const h of handles) {
              try { h?.remove?.(); } catch { /* ignore */ }
            }
          };
          // Token arrival
          PushNotifications.addListener("registration", (t) => {
            if (settled) return;
            settled = true;
            cleanup();
            resolve(t?.value || null);
          }).then((h) => handles.push(h)).catch(() => { /* listener add */ });
          // APNs registration failure
          PushNotifications.addListener("registrationError", (e) => {
            if (settled) return;
            settled = true;
            cleanup();
            reject(new Error(e?.error || "APNs registration failed"));
          }).then((h) => handles.push(h)).catch(() => { /* listener add */ });
          // Kick off the register flow. If permission was just granted
          // but APNs never responds (Apple Push offline, simulator,
          // missing entitlement), surface that within 10s rather than
          // hanging the UI.
          PushNotifications.register().catch((e) => {
            if (settled) return;
            settled = true;
            cleanup();
            reject(e);
          });
          setTimeout(() => {
            if (settled) return;
            settled = true;
            cleanup();
            reject(new Error("APNs registration timed out (check entitlement + provisioning)"));
          }, 10000);
        });

        if (!token) throw new Error("APNs returned empty token");

        const row = {
          user_id:       userId,
          endpoint:      token,           // APNs device token (hex)
          p256dh:        null,
          auth:          null,
          platform:      "ios",
          app_bundle_id: APP_BUNDLE_ID,
          device_label:  navigator.userAgent || "iOS device",
          user_agent:    navigator.userAgent || null,
          last_seen_at:  new Date().toISOString(),
        };
        const { error: err } = await supabase
          .from("push_subscriptions")
          .upsert(row, { onConflict: "user_id,endpoint" });
        if (err) throw err;

        setEnabled(true);
        return;
      }

      // ── Web branch (W3C Web Push) ──────────────────────────────
      // 1. Register SW if not already.
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;

      // 2. Ask for permission if we haven't yet. If denied, bail with
      //    a clean error instead of trying to subscribe anyway (which
      //    would just throw a less helpful error).
      let perm = Notification.permission;
      if (perm === "default") perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        throw new Error("Notification permission was not granted");
      }

      // 3. Subscribe. If one already exists (e.g., device was
      //    previously enabled for this same user), reuse it — fewer
      //    stale rows, faster path.
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        });
      }

      // 4. Persist. Using upsert so re-enabling on the same device
      //    doesn't create a second row when the endpoint is identical.
      const p256dh = sub.getKey("p256dh");
      const auth   = sub.getKey("auth");
      if (!p256dh || !auth) throw new Error("subscription missing keys");

      const row = {
        user_id:      userId,
        endpoint:     sub.endpoint,
        p256dh:       ab2b64url(p256dh),
        auth:         ab2b64url(auth),
        user_agent:   navigator.userAgent || null,
        last_seen_at: new Date().toISOString(),
      };
      const { error: err } = await supabase
        .from("push_subscriptions")
        .upsert(row, { onConflict: "user_id,endpoint" });
      if (err) throw err;

      setEnabled(true);
    } catch (e) {
      console.error("[push] enable failed:", e);
      setError(e.message || "Couldn't enable push");
      setEnabled(false);
    } finally {
      setBusy(false);
    }
  }, [supported, userId, busy]);

  const disable = useCallback(async () => {
    if (!supported || !userId || busy) return;
    setBusy(true);
    setError(null);
    try {
      // iOS branch: there's no "unsubscribe" on APNs the way Web Push
      // has one. We just drop the DB row(s) so send-push stops
      // targeting this device. The OS-level permission stays — user
      // can revoke from iOS Settings → Notifications if they want.
      if (IS_IOS_NATIVE) {
        await supabase
          .from("push_subscriptions")
          .delete()
          .eq("user_id", userId)
          .eq("platform", "ios");
        setEnabled(false);
        return;
      }

      const reg = await navigator.serviceWorker.getRegistration("/sw.js");
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        // Delete the DB row FIRST — if we unsubscribe and then the
        // DB delete fails, the edge function would still try to push
        // to the dead endpoint and get a 410. Reverse order avoids
        // that stale state.
        await supabase
          .from("push_subscriptions")
          .delete()
          .eq("user_id", userId)
          .eq("endpoint", sub.endpoint);
        await sub.unsubscribe();
      } else {
        // No browser-side subscription; still clean up any stale DB
        // rows pointing at this user + this UA. Defensive.
        await supabase
          .from("push_subscriptions")
          .delete()
          .eq("user_id", userId);
      }
      setEnabled(false);
    } catch (e) {
      console.error("[push] disable failed:", e);
      setError(e.message || "Couldn't disable push");
    } finally {
      setBusy(false);
    }
  }, [supported, userId, busy]);

  return { supported, permission, enabled, busy, error, enable, disable };
}
